import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { HttpBackendService } from 'src/app/core/backend-services/http/http.backend-service';

export interface NewsItem {
  id: number;
  title: string;
  content: string;
  summary?: string;
  authorUsername: string;
  publishedAt: string;
  /** only in the admin list; publishedAt stays set when a news is hidden */
  isPublished?: boolean;
  priority: number;
  tags: string[];
  imageURL?: string;
}

export interface NewsResponse {
  news: NewsItem[];
  pagination?: {
    page: number;
    totalPages: number;
    total: number;
    hasMore: boolean;
  };
}

export interface CreateNewsRequest {
  title: string;
  content: string;
  summary?: string;
  priority?: number;
  tags?: string[];
  imageURL?: string;
}

export interface UpdateNewsRequest {
  id: number;
  title?: string;
  content?: string;
  summary?: string;
  priority?: number;
  tags?: string[];
  imageURL?: string;
}

@Injectable({
  providedIn: 'root',
})
export class NewsBackendService {
  private readonly BASE_ADDRESS = '/news';
  private readonly ADMIN_ADDRESS = '/admin/news';

  constructor(private http: HttpBackendService) {}

  /**
   * @description get published news
   * @param {number} limit - max number of results (optional)
   * @param {number} page - page number for pagination (optional)
   * @returns {Promise<NewsResponse>}
   */
  public getPublishedNews(
    limit?: number,
    page?: number
  ): Promise<NewsResponse> {
    let url = `${this.BASE_ADDRESS}`;
    const params = new URLSearchParams();

    if (limit) {
      params.append('limit', limit.toString());
    }
    if (page) {
      params.append('page', page.toString());
    }

    if (params.toString()) {
      url += `?${params}`;
    }

    return firstValueFrom(this.http.get<NewsResponse>(url));
  }

  /**
   * @description get published news item
   * @param {number} id - specific news id (optional)
   * @returns {Promise<NewsItem>}
   */
  public getPublishedNewsItem(id: number): Promise<NewsItem> {
    const params = new URLSearchParams();
    params.append('id', id.toString());
    const url = `${this.BASE_ADDRESS}?${params}`;
    return firstValueFrom(this.http.get<NewsItem>(url));
  }

  /**
   * @description create new news item (admin only)
   * @param {CreateNewsRequest} newsData
   * @returns {Promise<NewsItem>}
   */
  public createNews(newsData: CreateNewsRequest): Promise<NewsItem> {
    return firstValueFrom(
      this.http.post<NewsItem>(`${this.ADMIN_ADDRESS}/create`, newsData)
    );
  }

  /**
   * @description get all news for admin (admin only)
   * @param {number} limit - max number of results (optional)
   * @param {number} page - page number for pagination (optional)
   * @returns {Promise<NewsResponse>}
   */
  public getAdminNews(limit?: number, page?: number): Promise<NewsResponse> {
    let url = `${this.ADMIN_ADDRESS}/list`;
    const params = new URLSearchParams();

    if (limit) {
      params.append('limit', limit.toString());
    }
    if (page) {
      params.append('page', page.toString());
    }

    if (params.toString()) {
      url += `?${params}`;
    }

    return firstValueFrom(this.http.get<NewsResponse>(url));
  }

  /**
   * @description update news item (admin only)
   * @param {UpdateNewsRequest} newsData
   * @returns {Promise<NewsItem>}
   */
  public updateNews(newsData: UpdateNewsRequest): Promise<NewsItem> {
    return firstValueFrom(
      this.http.put<NewsItem>(`${this.ADMIN_ADDRESS}/update`, newsData)
    );
  }

  /**
   * @description publish or unpublish news item (admin only)
   * @param {number} id - news item id
   * @param {boolean} isPublished - whether to publish or unpublish (true for publish)
   * @returns {Promise<NewsItem>}
   */
  public publishNews(id: number, isPublished: boolean): Promise<NewsItem> {
    return firstValueFrom(
      this.http.post<NewsItem>(`${this.ADMIN_ADDRESS}/publish`, {
        id,
        action: isPublished ? 'publish' : 'unpublish',
      })
    );
  }

  /**
   * @description delete news item (admin only)
   * @param {number} id - news item id
   * @returns {Promise<any>}
   */
  public deleteNews(id: number): Promise<any> {
    return firstValueFrom(
      this.http.delete<any>(`${this.ADMIN_ADDRESS}/delete/${id}`)
    );
  }
}
